<template>
  <div ref="waterjetStationView" class="station-view">
    <div class="waterjet-view" @click="focusBarcodeEntry">

      <div class="row no-gutters station-scanner-entry water-jet-topbar">
        <div class="col-5">
          <h2 class="text-muted">Enter panel barcode to scan a panel for {{productionStage.name}}</h2>
          <FormInputText ref="panelBarcodeScan" v-model="barcodeEntry" placeholder="Barcode" @enter="onScannedBarcode()" prependSlot>
            <template v-slot:prepend>
              <i class="far fa-scanner"></i>
            </template>
          </FormInputText>
        </div>
        <div class="col-3 pl-4">
          <h2>&nbsp;</h2>
          <div class="row no-gutters">
            <button class="btn btn-default col-3" @click.prevent="zoomOut"><i class="far fa-search-minus" /></button>
            <div class="col-6 pl-1 pr-1">
              <button class="btn btn-default btn-block" @click.prevent="zoomReset">{{zoomDisplayValue}}%</button>
            </div>
            <button class="btn btn-default col-3" @click.prevent="zoomIn"><i class="far fa-search-plus" /></button>
          </div>
        </div>
        <div class="col-4 pl-4">
          <h2 class="text-muted text-right">Panels pending scan out</h2>
          <button ref="scanOutQueueButton" class="btn btn-default btn-block text-muted" @click="showScanOutQueue">{{pendingScanOutList.length == 0 ? `Queue Empty` : `${pendingScanOutList.length} Panel(s)`}}</button>
        </div>
      </div>

      <!-- Waterjet Bed -->
      <div class="waterjet-bed-container">
        <WaterjetBed
          ref="waterjetBed"
          :bedBayList="bedBayList"
          :bedConfig="bedConfig"
          :cutoutOptionsAvailable="cutoutOptionsAvailable"
          :zoomScale="zoomScale"
          @updateScale="displayScaleUpdated($event)"
          @panelClicked="bedPanelClicked($event)"
        />
      </div>

      <!-- Waterjet Control Bar -->
      <div class="waterjet-control-bar">
        <div :class="['waterjet-panel', {'right-aligned': waterjetRightAligned}]" :style="waterjetPositionStyle(bay.position)" v-for="bay of bedBayList" :key="`${bay.index}-${bay.productionPanelId}-${waterjetUpdateKey}`">
          <template v-if="bay.productionPanelId">
            <div class="waterjet-panel-title text-muted">
              Position #{{bay.index+1}}&nbsp;&nbsp;:&nbsp;&nbsp;Panel {{bay.productionPanelData.barcode}}
            </div>
            <div class="waterjet-panel-buttons">
              <button class="btn btn-danger waterjet-button" @click.prevent="removePanelFromStation(bay.productionPanelId)"><i class="fal fa-times" /> Remove</button>
              <button class="btn btn-default waterjet-button" @click.prevent="panelInfoClicked(bay.productionPanelId, $event)"><i class="far fa-info-circle" /> Info</button>
              <button class="btn btn-success waterjet-button" v-if="bay.panelHasShapeData" @click.prevent="waterjetResetPanel(bay.productionPanelId)"><i class="far fa-power-off" /> Reset</button>
              <button :class="['btn', 'btn-success', 'waterjet-button', {'active': bay.panelIsFlipped}]" v-if="bay.panelHasShapeData" @click.prevent="waterjetFlipPanel(bay.productionPanelId)"><i class="far fa-border-center-v" /> {{bay.panelIsFlipped ? 'Flipped' : 'Flip'}}</button>
              <button class="btn btn-success waterjet-button" v-if="bay.panelHasShapeData" @click.prevent="waterjetRotatePanel(bay.productionPanelId, 1)"><i class="far fa-undo" /> Rotate</button>
              <button class="btn btn-success waterjet-button" v-if="bay.panelHasShapeData" @click.prevent="waterjetRotatePanel(bay.productionPanelId, -1)"><i class="far fa-undo fa-flip-horizontal" /> Rotate</button>
              <button class="btn btn-success waterjet-button" v-if="bay.panelHasShapeData" @click.prevent="waterjetOffsetPanelClicked(bay.productionPanelId, bay.index, $event)"><i class="far fa-arrows-alt" /> Offset</button>
              <button class="btn btn-success waterjet-button" v-if="waterjetCanSwapBays" @click.prevent="waterjetSwapBays()"><i class="far fa-exchange" /> Swap</button>
              <button class="btn btn-primary waterjet-button" v-if="bay.panelHasShapeData" @click.prevent="waterjetExportPanelDXF(bay.productionPanelId)"><i class="far fa-file-export" /> Export</button>
              <button class="btn btn-complete waterjet-button ml-1" @click.prevent="waterjetFlagPanelDone(bay.productionPanelId)"><i class="far fa-check-circle" /> Done</button>
            </div>
          </template>
          <template v-if="!bay.productionPanelId">
            <div class="waterjet-panel-title text-muted">Position #{{bay.index+1}}&nbsp;&nbsp;:&nbsp;&nbsp;Empty</div>
          </template>
        </div>
      </div>
    </div>

    <Popover ref="cutoutPopover" sizeClass="popover-medium" :showHeader="true" :headerTitle="selectedCutout.title"  placement="right">
      <div class="cutout-popover-content">
        <div class="row">
          <div class="col-12 station-popover-item-title font-weight-bold">Total Cutout Region</div>
        </div>
        <div class="row">
          <div class="col-4 station-popover-item-title">Cutout Height:</div>
          <div class="col-8 station-popover-item-value">{{selectedCutout.height}}mm</div>
        </div>
        <div class="row">
          <div class="col-4 station-popover-item-title">Cutout Width:</div>
          <div class="col-8 station-popover-item-value">{{selectedCutout.width}}mm</div>
        </div>
        <template v-if="selectedCutout.enabled && cutoutOptionsAvailable">
          <FormHorizontalRule />
          <div class="row">
            <div class="col-12 station-popover-item-title font-weight-bold">Cutting Order</div>
          </div>
          <div class="row">
            <div class="col-4"><button class="btn btn-default station-popover-button btn-block" :disabled="false" @click.prevent="cutoutMoveOrderUp"><i class="fas fa-caret-up" /></button></div>
            <div class="col-4 cutout-popover-cut-order-title">{{formatNumberOrdinal(parseInt(selectedCutout.cutOrder) + 1)}}</div>
            <div class="col-4"><button class="btn btn-default station-popover-button btn-block" :disabled="false" @click.prevent="cutoutMoveOrderDown"><i class="fas fa-caret-down" /></button></div>
          </div>
        </template>

        <div v-if="selectedCutout.enabled && selectedCutout.reliefCutList.length > 0">
          <div class="row mt-2">
            <div class="col-12 station-popover-item-title font-weight-bold">Relief Cuts</div>
          </div>          
          <template v-for="(reliefCut, index) of selectedCutout.reliefCutList">
            <div class="row" :key="`title-${index}-${reliefCut.type}`">
              <div class="col-4 station-popover-item-title">Relief Cut #{{index+1}}:</div>
              <div class="col-8 station-popover-item-value">{{formatLookupName(reliefCut.type, ProductionWaterjetReliefTypeLookup)}}</div>
            </div>
            <div class="row" :key="`msg-${index}-${reliefCut.type}`" v-if="!reliefCut.valid">
              <div class="col-4 station-popover-item-title"> </div>
              <div class="col-8 station-popover-item-value"><i class="far fa-exclamation-triangle mr-1 text-danger" v-if="!reliefCut.valid" /> {{reliefCut.errorMessage}}</div>
            </div>
          </template>
        </div>
        <div v-if="cutoutOptionsAvailable">
          <FormHorizontalRule />
          <div class="row no-gutters">
            <div class="col-3 pr-2">
              <button class="btn btn-warning station-popover-button btn-block" v-if="selectedCutout.enabled" @click.prevent="cutoutSetEnabledSetting(false)"><i class="fal fa-ban" /><br /> Disable<br />Cutout</button>
              <button class="btn btn-primary station-popover-button btn-block" v-if="!selectedCutout.enabled" @click.prevent="cutoutSetEnabledSetting(true)"><i class="fal fa-check-circle" /><br /> Enable<br />Cutout</button>
            </div>
            <div class="col-3 pr-2">
              <button class="btn btn-success station-popover-button btn-block" v-if="selectedCutout.enabled && selectedCutout.reliefCutAvailable" :disabled="!selectedCutout.isInlineReliefAvailable" @click.prevent="cutoutAddInlineReliefCut"><i class="fal fa-scalpel-path" /><br /> Add Relief<br />Inline</button>
            </div>
            <div class="col-3 pr-2">
              <button class="btn btn-success station-popover-button btn-block" v-if="selectedCutout.enabled && selectedCutout.reliefCutAvailable" :disabled="!selectedCutout.isLargeReliefAvailable" @click.prevent="cutoutAddLargeReliefCut"><i class="fal fa-clone" /><br /> Add Relief<br />Large</button>
            </div>
            <div class="col-3">
              <button class="btn btn-danger station-popover-button btn-block" v-if="selectedCutout.enabled && selectedCutout.reliefCutAvailable" :disabled="!selectedCutout.isRemoveReliefAvailable" @click.prevent="cutoutRemoveReliefCuts"><i class="fal fa-times" /><br /> Clear<br />Relief Cuts</button>
            </div>
          </div>
        </div>
      </div>
    </Popover>

    <Popover ref="offsetPanelPopover" sizeClass="popover-medium" :showHeader="true" headerTitle="Offset Panel"  placement="top">
      <div class="p-2">
        <div class="pl-4">
          <div class="row">
            <div class="col-3 offset-4">
              <button class="btn btn-default station-popover-button btn-block" @click.prevent="offsetPanel.offsetY++; panelOffsetUpdated()"><i class="fas fa-caret-up" /></button>
            </div>
          </div>
          <div class="row my-2">
            <div class="col-3 text-right offset-1">
              <button class="btn btn-default station-popover-button btn-block" @click.prevent="offsetPanel.offsetX--; panelOffsetUpdated()"><i class="fas fa-caret-left" /></button>
            </div>
            <div class="col-3">
            </div>
            <div class="col-3">
              <button class="btn btn-default station-popover-button btn-block" @click.prevent="offsetPanel.offsetX++; panelOffsetUpdated()"><i class="fas fa-caret-right" /></button>
            </div>
          </div>
          <div class="row">
            <div class="col-3 offset-4">
              <button class="btn btn-default station-popover-button btn-block" @click.prevent="offsetPanel.offsetY--; panelOffsetUpdated()"><i class="fas fa-caret-down" /></button>
            </div>
          </div>
        </div>
        <FormHorizontalRule />
        <div class="row">
          <div class="col-4"><FormStaticLabel>Horizontal</FormStaticLabel></div>
          <div class="col-8"><FormStaticLabel>{{offsetPanel.offsetX}} mm</FormStaticLabel></div>
        </div>
        <div class="row mt-1">
          <div class="col-4"><FormStaticLabel>Vertical</FormStaticLabel></div>
          <div class="col-8"><FormStaticLabel>{{offsetPanel.offsetY}} mm</FormStaticLabel></div>
        </div>
        <FormHorizontalRule />
        <div class="row">
          <div class="col-6">
            <StationControlPanelButton class="btn-success" sizeClass="wide-lg btn-block" iconStyle="fal" icon="fa-times" @click="resetPanelOffset">Reset</StationControlPanelButton>
          </div>
          <div class="col-6">
            <StationControlPanelButton class="btn-default" sizeClass="wide-lg btn-block" iconStyle="fal" icon="fa-times" @click="closePanelOffset">Close</StationControlPanelButton>
          </div>
        </div>

      </div>
    </Popover>

    <Popover
      ref="scanOutQueuePopover"
      sizeClass="popover-xlarge"
      :scrollable="true"
      :showHeader="true"
      headerTitle="Pending Scan Out"
      placement="bottom">
      <div class="px-2">
        <h2 class="text-muted text-center" v-if="pendingScanOutList.length == 0">
          Scan Out Queue Empty
        </h2>

        <QueuePanelListItem
          class="waterjet-queue-panel"
          v-for="panel of pendingScanOutList"
          :key="panel.id"
          :panel="panel"
          :highlightColour="productionStage.highlightColour"
          :currentStationId="productionStation.id"
          @removePanel="removePanelFromStation(panel.id)">
        </QueuePanelListItem>

      </div>
    </Popover>
  </div>
</template>

<style>
.waterjet-queue-panel {
  font-size: 12px !important;
}
.waterjet-queue-panel .btn {
  font-size: 12px !important;
}
</style>

<style lang="scss" scoped>
$waterjet-scanner-entry-height: 130px;
$waterjet-controlbar-height: 70px;

.cutout-popover-content {
  padding: 0.5rem;
}
.cutout-popover-cut-order-title {
  font-weight: bold;
  font-size: 24px;
  text-align: center;
}

.waterjet-view {
  flex: 1;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items:stretch;
}

.water-jet-topbar {
  height: $waterjet-scanner-entry-height;
}
.waterjet-bed-container {
  position: relative;
  display: flex;
  flex: calc(100% - #{$waterjet-scanner-entry-height} - #{$waterjet-controlbar-height});
}
.waterjet-control-bar {
  position: relative;
  height: $waterjet-controlbar-height;
  margin: 0;
  padding-top: 6px;
}
.waterjet-panel-title {
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
}
.waterjet-panel {
  position: absolute;
}
.waterjet-panel.right-aligned {
  width: 585px;
}
.waterjet-panel.right-aligned > .waterjet-panel-title {
  text-align: right;
}
.waterjet-panel.right-aligned > .waterjet-panel-buttons {
  text-align: right;
}
.waterjet-panel-buttons {
  margin-top: 2px;
}
.waterjet-button {
  width: 52px;
  height: 46px;
  padding: 0;
  margin-right: 0.4rem;
  font-size: 11px;
  text-transform: uppercase;
}
.waterjet-panel.right-aligned .waterjet-button {
  margin-right: 0.0rem;
  margin-left: 0.4rem;
}
.waterjet-button.btn-success.active {
  border-color: #74CBFC !important;
}
.waterjet-button > i {
  font-size: 1.2rem;
  display: block;
  padding: 4px 0px 5px 0px;
}

@media (max-width: 1399.98px) {
  .waterjet-button {
    width: 40px;
    font-size: 9px;
  }
  .waterjet-button > i {
    font-size: 1.0rem;
  }
}
</style>


<script>
import _ from 'lodash'
import { saveAs } from 'file-saver'

import formMixin from '@/mixins/form'
import Utils from '@/utils/utils'
import EventBus from '@/utils/eventBus'

import StationControlPanelButton from '@/apps/glass-production-station/src/components/buttons/stationControlPanelButton'
import ProductionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'
import QueuePanelListItem from '@/apps/glass-production-station/src/components/station/queuePanelListItem'
import WaterjetBed from '@/apps/glass-production-station/src/components/station/waterjetBed'
import FastForwardCompleteDialog from '@/apps/glass-production-station/src/views/fastForwardCompleteDialog'
import StationScanWarningDialog from '@/apps/glass-production-station/src/views/stationScanWarningDialog'
import WaterjetAddReliefCutDialog from '@/apps/glass-production-station/src/views/waterjetAddReliefCutDialog'

import Popover from '@/components/popover.vue'
import ProductionPanel from '@/apps/glass-production-station/src/classes/productionPanel'

import { DespatchTypeLookup, OrderEditorCutoutTypes, OrderEditorCutoutTypeLookup, ProductionWaterjetReliefTypes, ProductionWaterjetReliefTypeLookup, ProductionStationHardwareTypes } from '@/store/types'
import { AlignmentTypes } from '@/store/mathTypes'


export default {
  name: 'WaterjetStationView',
  mixins: [
    formMixin,
    ProductionStationMixin
  ],
  components: {
    QueuePanelListItem,
    WaterjetBed,
    Popover,
    StationControlPanelButton
  },
  props: {
    productionStage: Object,
    productionStation: Object
  },
  data() {
    return {
      barcodeEntry: '',
      panelScanSuccess: false,
      panelScanError: false,
      panelDetails: {
        barcode: '',
        nextStage: '',
        nextStageColour: '#400000',
        tasksComplete: false,
        despatchType: '',
        panelTaskStarted: false,
        panelTaskCompleted: false,
      },
      bedBayList: [],
      bedConfig: {
        width: 1000,
        height: 1000,
        positionList: [],
        homePositionX: 0,
        homePositionY: 0,
        originHorizontal: AlignmentTypes.left,
        originVertical: AlignmentTypes.bottom,
        originGuideHeight: 0,
        originGuideWidth: 0,
        maxNegativeHeight: 0,
        maxNegativeWidth: 0,

        displayScale: 1.0
      },
      selectedCutout: {
        title: 'Cut',
        enabled: true,
        cutOrder: 0,
        cutoutIndex: 0,
        bayIndex: 0,
        productionPanelId: null,
        width: 0,
        height: 0,
        reliefCutList: [],
        isCenterCutout: false,
        isInlineReliefAvailable: false,
        isLargeReliefAvailable: false,
        isRemoveReliefAvailable: false
      },
      offsetPanel: {
        productionPanelId: null,
        bayIndex: 0,
        offsetX: 0,
        offsetY: 0
      },
      comms: {
        // Not displayed anymore as spinner. Looked terrible
        // Here in case updated to show progress in future
        updatingServer: false,
        lastErrorMessage: ''
      },
      zoomScale: 1.0,
      zoomMinScale: 1.0,
      zoomMaxScale: 7.5,
      zoomWheelFactor: 0.001,
      waterjetUpdateKey: 1,

      panelScanErrorMessage: ''
    }
  },
  computed: {
    stationHasSecondaryStation() {
      return this.productionStation.secondaryStationId != null
    },
    secondaryStationName() {
      if(this.productionStation.secondaryStation)
        return this.productionStation.secondaryStation.name
      return ''
    },
    stationCanScanIn() {
      if(this.productionStation)
        return this.productionStation.isScanInAvailable
      return false
    },
    stationCanScanOut() {
      if(this.productionStation)
        return this.productionStation.isScanOutAvailable
      return false
    },
    waterjetPositionList() {
      return this.bedConfig.positionList || []
    },
    waterjetRightAligned() {
      return this.bedConfig.originHorizontal == AlignmentTypes.right
    },
    waterjetCanSwapBays() {
      return this.waterjetPositionList.length > 1
    },
    zoomDisplayValue() {
      return Math.round(this.zoomScale * 100.0)
    },
    pendingScanOutList() {
      if(this.productionStation) {
        // Any panel without waterjet station data is not on the bed & therefore pending scan out
        return this.productionStation.productionPanels.filter(panel => {
          let stationData = panel.stationData || {}
          let waterjetData = stationData.waterjetData || {}

          if(waterjetData.bayIndex === undefined || waterjetData.bayIndex < 0)
            return true
          return false
        })
      }
      return []
    },
    cutoutOptionsAvailable() {
      // Cutout options not available on generic waterjet devices
      // We just export the shape as-is for the most part on generic waterjets. User has to handle the rest
      return this.productionStation.hardwareType == ProductionStationHardwareTypes.waterjetJetstream
    }
  },
  watch: {
    'productionStation.productionPanels': {
      deep: true,
      handler: function() {
        this.updateBedPositions()
        this.updateWaterjetBed()
      }
    }
  },
  created() {
    this.ProductionWaterjetReliefTypeLookup = ProductionWaterjetReliefTypeLookup
    this.debouncedUpdateWaterjetPanelData = _.debounce(this._updateWaterjetPanelData, 500)
  },
  mounted() {
    this.$refs.panelBarcodeScan.select()
    this.$refs.panelBarcodeScan.focus()

    // Update bed configuration info. Must be done first
    this.updateBedConfig()

    this.updateBedPositions()
    this.updateWaterjetBed()
    
    this.$refs.waterjetStationView.addEventListener('mousewheel', this.onMouseWheel, false)
    this.$refs.waterjetStationView.addEventListener('mousedown', this.onMouseDown, false)
    this.$refs.waterjetStationView.addEventListener('mousemove', this.onMouseMove, false)
    this.$refs.waterjetStationView.addEventListener('mouseup', this.onMouseUp, false)

    window.addEventListener('resize', this.onResize, false)

    EventBus.on(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)
  },
  beforeDestroy() {
    this.$refs.waterjetStationView.removeEventListener('mousewheel', this.onMouseWheel)
    this.$refs.waterjetStationView.removeEventListener('mousedown', this.onMouseDown)
    this.$refs.waterjetStationView.removeEventListener('mousemove', this.onMouseMove)
    this.$refs.waterjetStationView.removeEventListener('mouseup', this.onMouseUp)

    window.removeEventListener('resize', this.onResize)

    EventBus.off(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)
  },
  methods: {
    onScannedBarcode(options = {}) {
      if(this.barcodeEntry.trim().length == '') {
        this.barcodeEntry = ''
        return
      }

      var scanData = {
        barcode: this.barcodeEntry
      }
      if(options)
        scanData = _.merge(scanData, options)

      // Run the scan in/out function on the panel scanned
      this.$apiService.glassProductionStationService.runPanelBarcodeScan(this.productionStation.id, scanData)
      .then(response => {
        let barcode = response.data.panelBarcode
        var nextStage = ''

        if(response.data.panelTaskCompleted) {
          if(response.data.nextStage && response.data.nextStage.productionStage) {
            nextStage = response.data.nextStage.productionStage.name
          }
        }

        if(response.data.tasksComplete) {
          var despatchName = Utils.getLookupName(DespatchTypeLookup, response.data.despatchType)
          this.displayPopupAlert('success', 'Panel Completed', `Panel ${barcode} has finished processing and is due at <strong>despatch</strong> for <strong>${despatchName}</strong>`)
        } else {
          if(response.data.panelTaskCompleted)
            this.displayPopupAlert('success', 'Panel Scanned Out', `Panel ${barcode} is next due at <strong>${nextStage}</strong>`)
        }

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()

        this.updateWaterjetBed()
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)
        
        // If the server gives fast forward options, display the fast forward dialog
        if(errorInfo.errorData && errorInfo.errorData.fastForwardOptions && errorInfo.errorData.fastForwardOptions.available) {

          // Show fast forward complete options
          this.displayModal(FastForwardCompleteDialog, { 
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id,
            fastForwardOptions: errorInfo.errorData.fastForwardOptions,
          })
          .then((result) => {

            if(result.allTasksCompleted) {
              // Attempt to automatically scan in the panel again now we have completed all pending tasks
              this.$nextTick(() => {
                this.onScannedBarcode()
              })
              return
            } else {
              this.displayPopupAlert('success', 'Tasks Completed', 'Pending Tasks completed, ready for scanning')
            }

            // Back to scanning
            this.barcodeEntry = ''
            this.$refs.panelBarcodeScan.focus()
          })          
          return
        }

         // If the server gives warnings + override, display the override warning dialog
        if(errorInfo.errorData && errorInfo.errorData.overrideWarningOptions && errorInfo.errorData.overrideWarningOptions.available) {

          // Show warning override options
          this.displayModal(StationScanWarningDialog, { 
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id,
            overrideWarningOptions: errorInfo.errorData.overrideWarningOptions,
          })
          .then((result) => {

            if(result) {
              // Attempt to scan again with override specified
              this.$nextTick(() => {
                this.onScannedBarcode({ overrideWarnings: true, overrideName: result.name })
              })
            }
          })          
          return
        }

        this.displayPopupAlert('error', 'Scanning Error!', errorInfo.errorMessage)

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })

    },
    removePanelFromStation(productionPanelId) {
      // Remove panel
      this.$apiService.glassProductionStationService.removePanelFromStation(this.productionStation.id, { productionPanelId: productionPanelId })
      .then(response => {

        this.updateWaterjetBed()

        this.$refs.panelBarcodeScan.select()
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)
        this.displayPopupAlert('error', 'Error Removing Panel!', errorInfo.errorMessage)

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
    },

    waterjetResetPanel(productionPanelId) {
      let bay = this.findBayForPanel(productionPanelId)
      if(bay) {
        bay.panelObj.waterjetReset(this.bedConfig)
        bay.panelObj.waterjetResetOriginPoint(this.bedConfig)
        bay.panelObj.recalculateCNCPaths(this.bedConfig)
        this.updateWaterjetBed()

        bay.panelIsFlipped = bay.panelObj.waterjetIsFlipped()

        // Send panel info to server
        this.debouncedUpdateWaterjetPanelData()
      }
    },
    waterjetFlipPanel(productionPanelId) {
      let bay = this.findBayForPanel(productionPanelId)
      if(bay) {
        bay.panelObj.waterjetToggleFlipped()
        bay.panelObj.waterjetResetOriginPoint(this.bedConfig)
        bay.panelObj.recalculateCNCPaths(this.bedConfig)
        this.updateWaterjetBed()

        bay.panelIsFlipped = bay.panelObj.waterjetIsFlipped()

        // Send panel info to server
        this.debouncedUpdateWaterjetPanelData()
      }
    },
    waterjetRotatePanel(productionPanelId, direction) {
      let bay = this.findBayForPanel(productionPanelId)
      if(bay) {
        // Change direction for flipped panels
        if(bay.panelObj.waterjetIsFlipped())
          direction = direction * -1

        bay.panelObj.waterjetRotate(direction)
        bay.panelObj.waterjetResetOriginPoint(this.bedConfig)
        bay.panelObj.recalculateCNCPaths(this.bedConfig)
        this.updateWaterjetBed()

        // Send panel info to server
        this.debouncedUpdateWaterjetPanelData()
      }
    },
    waterjetSwapBays() {
      if(this.bedBayList.length < 2)
        return

      // Rotate panels through the bays
      var bayPositions = this.bedBayList.map(bay => bay.position)
      this.bedBayList.unshift(this.bedBayList.pop())

      // Restore bay settings
      this.bedBayList.forEach((bay, index) => {
        bay.index = index
        bay.position = bayPositions[index]

        if(bay.panelObj) {
          bay.panelObj.waterjetBayPosition = bay.position
          bay.panelObj.waterjetResetOriginPoint(this.bedConfig)
          bay.panelObj.recalculateCNCPaths(this.bedConfig)
        }
      })

      this.updateWaterjetBed()

      // Send panel info to server
      this.debouncedUpdateWaterjetPanelData()
    },
    waterjetExportPanelDXF(productionPanelId) {
      try {
        let bay = this.findBayForPanel(productionPanelId)
        if(bay) {
          if(!bay.panelHasShapeData)
            return

          this.focusBarcodeEntry()

          // Generate export DXF
          let dxfData = bay.panelObj.generateCNCPathDXF(this.productionStation, this.bedConfig)

          // Download
          var dxfBlob = new Blob([dxfData], {
            type: 'application/dxf'
          });
          
          // Cleanup barcode to remove leading zeros for station operator to find the files easier
          // Note: Assumes the barcode starts with a 'P'
          let barcode = '' + bay.panelObj.barcode
          if(barcode.substr(0, 1).toUpperCase() == 'P') {
            barcode = 'P' + parseInt(barcode.substring(1))
          }
          
          var filename = `${barcode}.dxf`

          // Upload to server with DXF contents for reference
          this.uploadWaterjetExportData(productionPanelId, filename, dxfData)

          saveAs(dxfBlob, filename)

          this.displayPopupAlert('success', 'Export DXF Created', `Waterjet DXF created as:<br /><br />${filename}`)
          .then(() => {
            this.$nextTick(() => {
              this.focusBarcodeEntry()
            })
          })
        }
      } catch(err) {
        this.displayExceptionErrorAlert('Export Error', err)
      }
    },
    uploadWaterjetExportData(productionPanelId, dxfFilename, dxfContents) {
      var exportData = {
        productionPanelId: productionPanelId,
        filename: dxfFilename,
        exportData: dxfContents,
        description: 'DXF Export at waterjet station'
      }

      // Upload in background silently
      this.$apiService.glassProductionStationService.updatePanelExportData(this.productionStation.id, exportData)
      .then(() => {
      })
      .catch(err => {
        // Quietly handle error. Don't prevent exporting locally
        console.error(err)
      })
    },
    waterjetFlagPanelDone(productionPanelId) {
      let bay = this.findBayForPanel(productionPanelId)
      if(bay) {
        let stationPanel = this.productionStation.productionPanels.find(panel => panel.id == productionPanelId)
        if(stationPanel) {

          // Clear waterjet data from panel on server & locally
          // It'll show up in the pending scan out queue if it has not valid waterjet data
          stationPanel.stationData = {}
          var updateData = {
            productionPanelId: productionPanelId,
            stationData: {
              waterjetData: {
                bayIndex: -1
              }
            }
          }

          this.focusBarcodeEntry()

          this.comms.updatingServer = true
          this.$apiService.glassProductionStationService.updatePanelStationData(this.productionStation.id, updateData)
          .then(response => {
            this.updateBedPositions()
            this.updateWaterjetBed()
          })
          .catch(err => {
            let errorInfo = this.processFormResponse(err)
            this.displayPopupAlert('error', 'Error Finishing Panel!', errorInfo.errorMessage)
          })
          .finally(() => {
            this.comms.updatingServer = false
          })
        }
      }
    },

    bedPanelClicked(panelClickEvent) {
      if(panelClickEvent.panelClickInfo.type == 'cutout') {
        let cutout = panelClickEvent.panelClickInfo.cutout

        this.selectedCutout.title = Utils.getLookupName(OrderEditorCutoutTypeLookup, cutout.type) + ' Path'
        this.selectedCutout.cutoutIndex = panelClickEvent.panelClickInfo.cutoutIndex
        this.selectedCutout.cutOrder = cutout.cutOrder
        this.selectedCutout.bayIndex = panelClickEvent.bayIndex
        this.selectedCutout.productionPanelId = panelClickEvent.panel.id
        this.selectedCutout.width = Math.round(cutout.sourceCutoutBounds.max.x - cutout.sourceCutoutBounds.min.x)
        this.selectedCutout.height = Math.round(cutout.sourceCutoutBounds.max.y - cutout.sourceCutoutBounds.min.y)
        
        this.selectedCutout.enabled = cutout.enabled
        this.selectedCutout.isCenterCutout = cutout.type == OrderEditorCutoutTypes.centerCutOut
        this.selectedCutout.isInlineReliefAvailable = false
        this.selectedCutout.isLargeReliefAvailable = false
        this.selectedCutout.isRemoveReliefAvailable = false
        this.selectedCutout.reliefCutList = []
        this.selectedCutout.reliefCutAvailable = false

        // Set options for cutouts
        // Currently only allow 1 relief cut of each type per edge/corner cutout
        let panelObj = this.bedBayList[this.selectedCutout.bayIndex].panelObj
        if(panelObj) {

          // Don't allow relief cuts for generic waterjet machines
          // Due to us just exporting the shape path, and inline paths are part of the cutouts themselves when they are exported seperately
          var reliefCutsAvailable = false
          if(this.productionStation.hardwareType == ProductionStationHardwareTypes.waterjetJetstream)
            reliefCutsAvailable = true

          let curReliefCuts = panelObj.getWaterjetReliefCuts(panelClickEvent.panelClickInfo.cutoutIndex)
          let inlineCount = curReliefCuts.filter(relief => relief.type == ProductionWaterjetReliefTypes.inline).length
          let largeCount = curReliefCuts.filter(relief => relief.type == ProductionWaterjetReliefTypes.large).length

          if(reliefCutsAvailable) {
            this.selectedCutout.reliefCutList = curReliefCuts

            if(!this.selectedCutout.isCenterCutout) {
              this.selectedCutout.reliefCutAvailable = true
              this.selectedCutout.isInlineReliefAvailable = inlineCount == 0
              this.selectedCutout.isLargeReliefAvailable = largeCount == 0
              this.selectedCutout.isRemoveReliefAvailable = inlineCount > 0 || largeCount > 0
            }
          }

          this.$refs.cutoutPopover.displayFromPosition(panelClickEvent.event.clientX, panelClickEvent.event.clientY)
        }
      }
      else {
        this.$refs.cutoutPopover.hidePopover()
      }

      this.$refs.offsetPanelPopover.hidePopover()
    },
    cutoutAddInlineReliefCut(ev) {
      ev.stopPropagation()
      ev.preventDefault()

      let panelObj = this.bedBayList[this.selectedCutout.bayIndex].panelObj
      if(panelObj) {
        let largestBound = Math.round(Math.max(this.selectedCutout.width/2, this.selectedCutout.height/2))

        this.displayModal(WaterjetAddReliefCutDialog, { 
          isInLineReliefCut: true,
          maxDistanceFromEdge: largestBound
        })
        .then(data => {       
          if(data) {    
            panelObj.addWaterjetInlineReliefCut(this.selectedCutout.cutoutIndex, data.distanceFromEdge, data.reliefCutHeight)
            panelObj.recalculateCNCPaths(this.bedConfig)
            this.updateWaterjetBed()
          }
        })
      }

      this.$refs.cutoutPopover.hidePopover()
    },
    cutoutAddLargeReliefCut(ev) {
      ev.stopPropagation()
      ev.preventDefault()

      let panelObj = this.bedBayList[this.selectedCutout.bayIndex].panelObj
      if(panelObj) {
        let smallestBound = Math.round(Math.min(this.selectedCutout.width/2, this.selectedCutout.height/2))

        this.displayModal(WaterjetAddReliefCutDialog, { 
          isInLineReliefCut: false,
          maxDistanceFromEdge: smallestBound
        })
        .then(data => {       
          if(data) {    
            panelObj.addWaterjetLargeReliefCut(this.selectedCutout.cutoutIndex, data.distanceFromEdge)
            panelObj.recalculateCNCPaths(this.bedConfig)
            this.updateWaterjetBed()
          }
        })
      }

      this.$refs.cutoutPopover.hidePopover()
    },
    cutoutRemoveReliefCuts() {
      let panelObj = this.bedBayList[this.selectedCutout.bayIndex].panelObj
      if(panelObj) {
        panelObj.removeWaterjetReliefCuts(this.selectedCutout.cutoutIndex)
        panelObj.recalculateCNCPaths(this.bedConfig)
      }

      this.$refs.cutoutPopover.hidePopover()
      this.updateWaterjetBed()
    },
    cutoutSetEnabledSetting(enabled) {
      let panelObj = this.bedBayList[this.selectedCutout.bayIndex].panelObj
      if(panelObj) {
        panelObj.setWaterjetCutoutEnabled(this.selectedCutout.cutoutIndex, enabled)
        panelObj.recalculateCNCPaths(this.bedConfig)
      }

      this.$refs.cutoutPopover.hidePopover()
      this.updateWaterjetBed()
    },
    cutoutMoveOrderUp() {
      let panelObj = this.bedBayList[this.selectedCutout.bayIndex].panelObj
      if(panelObj) {
        panelObj.changeCutoutOrder(this.selectedCutout.cutoutIndex, -1)

        this.selectedCutout.cutOrder = panelObj.calculatedCutoutPaths[this.selectedCutout.cutoutIndex].cutOrder
        this.updateWaterjetBed()
      }
    },
    cutoutMoveOrderDown() {
      let panelObj = this.bedBayList[this.selectedCutout.bayIndex].panelObj
      if(panelObj) {
        panelObj.changeCutoutOrder(this.selectedCutout.cutoutIndex, 1)

        this.selectedCutout.cutOrder = panelObj.calculatedCutoutPaths[this.selectedCutout.cutoutIndex].cutOrder
        this.updateWaterjetBed()
      }
    },

    panelInfoClicked(productionPanelId, evt) {
      let panelData = this.productionStation.productionPanels.find(panel => panel.id == productionPanelId)
      if(!panelData)
        return

      this.$nextTick(() => {
        this.$emit('panelClick', { panel: panelData, target: evt.target, forceElement: true })
      })
    },
    waterjetOffsetPanelClicked(productionPanelId, bayIndex, evt) {
      let panelData = this.productionStation.productionPanels.find(panel => panel.id == productionPanelId)
      if(!panelData)
        return
      
      let panelObj = this.bedBayList[bayIndex].panelObj
      if(!panelObj)
        return

      this.offsetPanel.productionPanelId = panelData.id
      this.offsetPanel.bayIndex = bayIndex
      this.offsetPanel.offsetX = parseInt(panelObj.waterjetOriginOffsetX || 0)
      this.offsetPanel.offsetY = parseInt(panelObj.waterjetOriginOffsetY || 0)

      this.$nextTick(() => {
        this.$refs.offsetPanelPopover.displayFromElement(evt.target)
      })
    },
    resetPanelOffset() {
      this.offsetPanel.offsetX = 0
      this.offsetPanel.offsetY = 0

      this.panelOffsetUpdated()
    },
    closePanelOffset() {
      this.$refs.offsetPanelPopover.hidePopover()
    },
    panelOffsetUpdated() {
      // Set panel info
      let panelObj = this.bedBayList[this.offsetPanel.bayIndex].panelObj
      if(panelObj) {
        panelObj.waterjetOriginOffsetX = this.offsetPanel.offsetX;
        panelObj.waterjetOriginOffsetY = this.offsetPanel.offsetY;

        panelObj.recalculateCNCPaths(this.bedConfig)
      }

      this.debouncedUpdateWaterjetPanelData()
      this.updateWaterjetBed()
    },

    onResize() {
      this.updateWaterjetBed()
    },

    updateWaterjetBed() {
      this.$nextTick(() => {
        this.$refs.waterjetBed.render()
      })
    },

    updateBedConfig() {
      if(!this.productionStation)
        return

      let bedConfig = this.productionStation.waterjetBedConfig || {}
      this.bedConfig.width = parseInt(bedConfig.width || 1000)
      this.bedConfig.height = parseInt(bedConfig.height || 1000)
      this.bedConfig.positionList = bedConfig.positionList || []
      this.bedConfig.homePositionX = parseInt(bedConfig.homePositionX || 0)
      this.bedConfig.homePositionY = parseInt(bedConfig.homePositionY || 0)
      this.bedConfig.originHorizontal = bedConfig.originHorizontal || AlignmentTypes.left
      this.bedConfig.originVertical = bedConfig.originVertical || AlignmentTypes.bottom
      this.bedConfig.originGuideHeight = parseInt(bedConfig.originGuideHeight || 1000)
      this.bedConfig.originGuideWidth = parseInt(bedConfig.originGuideWidth || 1000)
      this.bedConfig.maxNegativeHeight = parseInt(bedConfig.maxNegativeHeight || 1000)
      this.bedConfig.maxNegativeWidth = parseInt(bedConfig.maxNegativeWidth || 1000)

      if(this.bedConfig.positionList.length == 0)
        this.bedConfig.positionList = [0]

      this.bedBayList = this.bedConfig.positionList.map((position, index) => {
        return {
          index: index,
          position: position,
          productionPanelId: null,
          productionPanelData: null,
          panelObj: null,
          panelHasShapeData: false,
          panelIsFlipped: false
        }
      })
    },

    updateBedPositions() {
      let hardwareConfigList = this.productionStation.productionStationHardwareConfigs || []

      // Fill in the panel details for each bed position
      this.bedBayList.forEach((bay, index) => {
        // Find out what panel is at this bay
        var bayPanelObj = this.productionStation.productionPanels.find(panel => {
          let stationData = panel.stationData || {}
          let waterjetData = stationData.waterjetData || {}

          if(waterjetData.bayIndex === undefined)
            return false
          return waterjetData.bayIndex == bay.index
        })

        if(!bayPanelObj) {
          bay.productionPanelId = null
          bay.productionPanelData = null
          bay.panelObj = null
        } else {
          // Only update if not already setup
          if(bayPanelObj.id != bay.productionPanelId) {
            bay.productionPanelId = bayPanelObj.id
            bay.productionPanelData = _.cloneDeep(bayPanelObj)
            bay.panelHasShapeData = bayPanelObj.orderItem && bayPanelObj.orderItem.shapeData && bayPanelObj.orderItem.shapeData.cutouts && bayPanelObj.orderItem.shapeData.edgePoints

            let orderItem = bayPanelObj.orderItem || {}
            let stockInfo = orderItem.stock || {}
            let panelThickness = parseFloat(stockInfo.thickness || 0)

            // Find the hardware config records that match the panel thickness
            let stationPanelHWConfig = hardwareConfigList.find(hwConfig => {
              return (panelThickness >= hwConfig.minThickness && panelThickness < hwConfig.maxThickness)
            })

            bay.panelObj = new ProductionPanel(bayPanelObj, stationPanelHWConfig)
            bay.panelObj.waterjetReset(this.bedConfig)

            // Read in the waterjet station data
            let stationData = bayPanelObj.stationData || {}
            let waterjetData = stationData.waterjetData || {}

            let rotationEdgeIndex = parseInt(waterjetData.rotationEdgeIndex || 0)

            // If the rotation edge index is -1 (default from server)
            // Then find the best default rotation for the panel (longest edge along the width)
            if(rotationEdgeIndex == -1) {
              waterjetData.rotationEdgeIndex = bay.panelObj.calculateDefaultRotationEdge(this.bedConfig)                            
            }

            bay.panelObj.deserializeWaterjetData(waterjetData)
            bay.panelObj.waterjetBayPosition = bay.position
            bay.panelObj.waterjetResetOriginPoint(this.bedConfig)
            bay.panelObj.recalculateCNCPaths(this.bedConfig)

            bay.panelIsFlipped = bay.panelObj.waterjetIsFlipped()
          }
        }
      })

      this.waterjetUpdateKey++
    },

    findBayForPanel(productionPanelId) {
      return this.bedBayList.find(bay => bay.productionPanelId == productionPanelId)
    },

    displayScaleUpdated(scale) {
      this.bedConfig.scale = scale
      this.waterjetUpdateKey++
    },

    waterjetPositionStyle(offset) {
      let rulerSize = 20

      if(this.bedConfig.originHorizontal == AlignmentTypes.left) {
        return {
          left: Math.round(rulerSize + offset * this.bedConfig.scale) + 'px'
        }
      } else if(this.bedConfig.originHorizontal == AlignmentTypes.right) {
        return {
          right: Math.round(rulerSize + offset * this.bedConfig.scale) + 'px'
        }
      }
    },

    _updateWaterjetPanelData() {
      // Send the panels in the bays station data to the server
      this.bedBayList.forEach(bay => {
        if(!bay.productionPanelId || !bay.panelObj)
          return

        var waterjetData = bay.panelObj.serializeWaterjetData()
        waterjetData.bayIndex = bay.index

        var updateData = {
          productionPanelId: bay.productionPanelId,
          stationData: {
            waterjetData: waterjetData
          }
        }

        this.comms.updatingServer = true
        this.$apiService.glassProductionStationService.updatePanelStationData(this.productionStation.id, updateData)
        .then(response => {
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.comms.updatingServer = false
        })
      })
    },
    focusBarcodeEntry() {
      // Ignore if a modal dialog if open
      if(this.isDocumentModalVisible())
        return

      // Select barcode entry to ensure it always has focus for barcode scanner
      this.$refs.panelBarcodeScan.focus()
    },

    zoomOut() {
      this.zoomScale = Math.max(this.zoomScale - 0.5, this.zoomMinScale)
    },
    zoomIn() {
      this.zoomScale = Math.min(this.zoomScale + 0.5, this.zoomMaxScale)
    },
    zoomReset() {
      this.zoomScale = 1.0
      this.$refs.waterjetBed.resetPan()
    },
    showScanOutQueue(ev) {
      this.$refs.scanOutQueuePopover.displayFromElement(this.$refs.scanOutQueueButton)
    },

    onMouseWheel(ev) {
      if(ev.target == this.$refs.waterjetBed.getWaterjetBedCanvas()) {        
        // Disabled until can pan with zoom
        //this.zoomScale += ev.wheelDeltaY * this.zoomWheelFactor
        //this.zoomScale = Math.min(this.zoomScale, this.zoomMaxScale)
        //this.zoomScale = Math.max(this.zoomScale, this.zoomMinScale)

        ev.stopPropagation()
      }
    },
    onMouseDown(ev) {
      if(ev.target == this.$refs.waterjetBed.getWaterjetBedCanvas()) {
        this.$refs.waterjetBed.onMouseDown(ev)
        ev.stopPropagation()
      }
    },
    onMouseMove(ev) {
      if(ev.target == this.$refs.waterjetBed.getWaterjetBedCanvas()) {
        this.$refs.waterjetBed.onMouseMove(ev)
      }
    },
    onMouseUp(ev) {
      if(ev.target == this.$refs.waterjetBed.getWaterjetBedCanvas()) {
        this.$refs.waterjetBed.onMouseUp(ev)
      }
    },
  }
  
}
</script>